
import { PartFilter } from "@/data/parts";
import { Settings, Printer, Wrench, Zap, ShoppingCart } from "lucide-react";

// Define a custom interface for our icon objects
export interface IconConfig {
  type: any;  // The icon component type
  props: {
    size: number;
    className?: string;
  };
}

export interface RequirementItemConfig {
  active: boolean;
  label: string;
  tooltip: string;
  filter: PartFilter;
  icon: IconConfig;
  activeColor: string;
}

interface RequirementBaseConfig {
  icon: any; // The icon component
  activeColor: string;
  activeText: string;
  inactiveText: string;
  activeTooltip: string;
  inactiveTooltip: string;
  filter: PartFilter;
}

// Define base configuration for all requirement types
const requirementBaseConfigs: Record<string, RequirementBaseConfig> = {
  root: {
    icon: Settings,
    activeColor: "text-red-500",
    activeText: "Root access required",
    inactiveText: "No Root Access Required",
    activeTooltip: "Root access is needed to make changes to the printer configuration file.",
    inactiveTooltip: "No changes to the printer configuration file are needed.",
    filter: "requires-root" as PartFilter
  },
  tools: {
    icon: Wrench,
    activeColor: "text-purple-600 dark:text-[#D946EF]",
    activeText: "Tools required",
    inactiveText: "No Additional Tools Required",
    activeTooltip: "Additional tools which did not come with your printer are required.",
    inactiveTooltip: "Does not require tools other than what came with your printer.",
    filter: "requires-tools" as PartFilter
  },
  wiring: {
    icon: Zap,
    activeColor: "text-yellow-400",
    activeText: "Wiring required",
    inactiveText: "No Wiring Required",
    activeTooltip: "Not plug and play - requires wiring or soldering.",
    inactiveTooltip: "Plug and play - does not require additional wiring or soldering.",
    filter: "wiring" as PartFilter
  },
  printing: {
    icon: Printer,
    activeColor: "text-blue-500 dark:text-[#0EA5E9]",
    activeText: "Printing required",
    inactiveText: "No Printed Parts Required",
    activeTooltip: "Requires printed parts which can withstand high temperatures, such as ASA, ABS, PC or PA.",
    inactiveTooltip: "Does not require printed parts which can withstand high temperatures.",
    filter: "requires-printing" as PartFilter
  },
  purchase: {
    icon: ShoppingCart,
    activeColor: "text-lime-600 dark:text-green-400",
    activeText: "Purchase required",
    inactiveText: "No Purchased Parts Required",
    activeTooltip: "This is a purchased part or requires purchased parts.",
    inactiveTooltip: "Does not require purchased parts.",
    filter: "requires-purchase" as PartFilter
  }
};

// Create icon factory function
const createIcon = (IconComponent: any, size: number, className?: string): IconConfig => {
  return {
    type: IconComponent,
    props: { size, className }
  };
};

export const getRequirementItemConfig = (
  requiresRoot: boolean,
  requiresWiring: boolean,
  requiresPrinting: boolean,
  requiresTools: boolean,
  requiresPurchase: boolean,
  printedParts?: string,
  hasShoppingLinks?: boolean,
  subcategory?: string,
): Record<string, RequirementItemConfig> => {
  // Define inactive color - darker gray
  const inactiveColor = "text-gray-400 dark:text-gray-600";
  
  // Helper function to get class name based on active state
  const getIconClassName = (isActive: boolean, activeColor: string): string => {
    return isActive ? activeColor : inactiveColor;
  };

  const result: Record<string, RequirementItemConfig> = {};
  
  // Create config for each requirement type
  for (const [key, baseConfig] of Object.entries(requirementBaseConfigs)) {
    let isActive = false;
    
    // Determine if this requirement is active
    switch (key) {
      case "root":
        isActive = requiresRoot;
        break;
      case "wiring":
        isActive = requiresWiring;
        break;
      case "printing":
        isActive = !!(requiresPrinting || printedParts);
        break;
      case "tools":
        isActive = !!(requiresTools || subcategory === "tools");
        break;
      case "purchase":
        isActive = !!(requiresPurchase || hasShoppingLinks);
        break;
    }
    
    // Create the configuration for this requirement
    result[key] = {
      active: isActive,
      label: isActive ? baseConfig.activeText : baseConfig.inactiveText,
      tooltip: `${isActive ? baseConfig.activeTooltip : baseConfig.inactiveTooltip} Click to toggle.`,
      filter: baseConfig.filter,
      icon: createIcon(
        baseConfig.icon, 
        14, 
        getIconClassName(isActive, baseConfig.activeColor)
      ),
      activeColor: isActive ? baseConfig.activeColor : inactiveColor
    };
  }

  return result;
};
